import {
  BRAND_INTERPOLATION_VAR,
  LEGACY_BRAND_NAME,
} from '@brightdrop/bd-ui/brand/constants';
import {
  buildPostProcessorModule,
  buildPseudoI18nOptions,
} from '@brightdrop/localization-client';
import { SUPPORTED_LOCALES } from '@brightdrop/localization-core';
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { getStoredLocale } from '~/common/utils/signed-out-locale.utils';
import TranslationBundle from '../translations/index';

const loggedMissingKeys = new Set<string>();

// Registered before `init` so the language detected on startup is applied too.
// Reads the instance rather than the payload: BrandI18nSync emits a bare
// `languageChanged` to re-render consumers without changing the language.
i18next.on('languageChanged', () => {
  document.documentElement.lang = i18next.language;
});

i18next
  .use(buildPostProcessorModule())
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: TranslationBundle,
    // A language picked before sign-in outranks the detector until a profile
    // loads and its language takes over.
    lng: getStoredLocale(),
    defaultNS: 'common',
    fallbackNS: 'common',
    debug: false,
    saveMissing: globalThis.location?.hostname === 'localhost',
    missingKeyHandler: (_lngs, ns, key, fallbackValue) => {
      const fullKey = `${ns}:${key}`;
      if (loggedMissingKeys.has(fullKey)) {
        return;
      }

      loggedMissingKeys.add(fullKey);

      console.warn('[i18next] missingKey', {
        language: i18next.language,
        namespace: ns,
        key: fullKey,
        fallback: fallbackValue,
      });
    },
    interpolation: {
      escapeValue: false,
      skipOnVariables: true,
      // Default brand label for `{{brandName}}` copy; legacy until the rebrand
      // flag resolves, then updated reactively by BrandI18nSync.
      defaultVariables: {
        [BRAND_INTERPOLATION_VAR]: LEGACY_BRAND_NAME,
      },
    },
    ...buildPseudoI18nOptions(
      {
        en: ['en-US'],
        'en-CA': ['en-US'],
        fr: ['fr-CA', 'en-US'],
        'fr-CA': ['en-US'],
        es: ['es-MX', 'en-US'],
        'es-MX': ['en-US'],
        default: ['en-US'],
      },
      // A copy, because the pseudo locale is appended to the array in dev.
      [...SUPPORTED_LOCALES],
      // The shell is the only writer of `locale`. Remotes keep the helper
      // default, which reads the cookie and does not cache.
      {
        order: ['cookie', 'navigator'],
        lookupCookie: 'locale',
        caches: ['cookie'],
        cookieOptions: { secure: true, sameSite: 'strict' },
      }
    ),
  });

export default i18next;
